import { useCallback, useEffect, useState } from 'react';

export const NAVIGATION_LAYOUT_STORAGE_KEY = 'adminNavigationLayout';
const NAVIGATION_LAYOUT_CHANGE_EVENT = 'adminNavigationLayoutChange';

export const NAVIGATION_LAYOUTS = {
  HEADER: 'header',
  SIDEBAR: 'sidebar',
} as const;

export type NavigationLayout = (typeof NAVIGATION_LAYOUTS)[keyof typeof NAVIGATION_LAYOUTS];

const DEFAULT_NAVIGATION_LAYOUT = NAVIGATION_LAYOUTS.HEADER;

export function isNavigationLayout(value: unknown): value is NavigationLayout {
  return value === NAVIGATION_LAYOUTS.HEADER || value === NAVIGATION_LAYOUTS.SIDEBAR;
}

function readStoredNavigationLayout(): NavigationLayout {
  try {
    const item = window.localStorage.getItem(NAVIGATION_LAYOUT_STORAGE_KEY);
    const parsed = item ? JSON.parse(item) : DEFAULT_NAVIGATION_LAYOUT;

    return isNavigationLayout(parsed) ? parsed : DEFAULT_NAVIGATION_LAYOUT;
  } catch (error) {
    console.warn(`Error reading localStorage key "${NAVIGATION_LAYOUT_STORAGE_KEY}":`, error);
    return DEFAULT_NAVIGATION_LAYOUT;
  }
}

export default function useNavigationLayoutPreference() {
  const [navigationLayout, setStoredNavigationLayout] = useState<NavigationLayout>(readStoredNavigationLayout);

  const setNavigationLayout = useCallback((layout: NavigationLayout) => {
    try {
      window.localStorage.setItem(NAVIGATION_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
    } catch (error) {
      console.warn(`Error setting localStorage key "${NAVIGATION_LAYOUT_STORAGE_KEY}":`, error);
    }

    setStoredNavigationLayout(layout);
    window.dispatchEvent(new CustomEvent<NavigationLayout>(NAVIGATION_LAYOUT_CHANGE_EVENT, { detail: layout }));
  }, []);

  useEffect(() => {
    const syncNavigationLayout = () => {
      setStoredNavigationLayout(readStoredNavigationLayout());
    };

    const handleLayoutChangeEvent = (event: Event) => {
      const { detail } = event as CustomEvent<NavigationLayout>;

      setStoredNavigationLayout(isNavigationLayout(detail) ? detail : readStoredNavigationLayout());
    };

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === NAVIGATION_LAYOUT_STORAGE_KEY) {
        syncNavigationLayout();
      }
    };

    window.addEventListener(NAVIGATION_LAYOUT_CHANGE_EVENT, handleLayoutChangeEvent);
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener(NAVIGATION_LAYOUT_CHANGE_EVENT, handleLayoutChangeEvent);
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);

  return [navigationLayout, setNavigationLayout] as const;
}
